import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { dataset } from "@/data";
import { agentScores } from "@/data/scores";
import { search } from "@/engine/search";
import { ScoreBar, StatusDot, Avatar, Coverage } from "@/components/ui";
import type { usePins } from "@/hooks";

const CATEGORY_COLS = ["shipping", "reliability", "fees", "warehouse", "support", "qc"] as const;

type SortKey = "overall" | "name" | "trustpilot" | (typeof CATEGORY_COLS)[number];

export default function HomePage({ pins }: { pins: ReturnType<typeof usePins> }) {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const statusFilter = params.get("status") ?? "all";
  const sort = (params.get("sort") ?? "overall") as SortKey;
  const dir = (params.get("dir") ?? "desc") as "asc" | "desc";

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  const toggleSort = (k: SortKey) => {
    if (sort === k) set("dir", dir === "desc" ? "asc" : "desc");
    else {
      const next = new URLSearchParams(params);
      next.set("sort", k);
      next.set("dir", k === "name" ? "asc" : "desc");
      setParams(next, { replace: true });
    }
  };

  const rows = useMemo(() => {
    let agents = dataset.agents;
    if (statusFilter !== "all") agents = agents.filter((a) => (a.status.v ?? "unknown") === statusFilter);

    const searched = search(agents, q, (a) => [a.name, ...a.aliases]);
    const withScores = searched.map(({ item }) => {
      const s = agentScores.get(item.id)!;
      const cat = (id: string) => s.categories.find((c) => c.id === id)?.score ?? null;
      return {
        agent: item,
        overall: s.overall,
        coverage: s.overallCoverage,
        tp: item.trustpilot.v?.score ?? null,
        cats: Object.fromEntries(CATEGORY_COLS.map((c) => [c, cat(c)])) as Record<string, number | null>,
      };
    });

    const val = (r: (typeof withScores)[number]): number | string => {
      if (sort === "name") return r.agent.name.toLowerCase();
      if (sort === "overall") return r.overall ?? -1;
      if (sort === "trustpilot") return r.tp ?? -1;
      return r.cats[sort] ?? -1;
    };
    withScores.sort((a, b) => {
      const av = val(a), bv = val(b);
      const cmp = typeof av === "string" ? av.localeCompare(bv as string) : (av as number) - (bv as number);
      return dir === "asc" ? cmp : -cmp;
    });
    return withScores;
  }, [q, statusFilter, sort, dir]);

  const arrow = (k: SortKey) => (sort === k ? <span className="sort-ind">{dir === "asc" ? "▲" : "▼"}</span> : null);

  return (
    <div>
      <div className="row spread wrap" style={{ marginBottom: 6 }}>
        <div>
          <h1>Shopping agent rankings</h1>
          <p className="muted" style={{ margin: 0 }}>
            {dataset.agents.length} agents · deterministic scores · pick agents to compare side-by-side.
          </p>
        </div>
      </div>

      <div className="card card-pad mt" style={{ marginBottom: 14 }}>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="searchbar">
            <input
              className="input"
              placeholder="Search agents or aliases…"
              value={q}
              onChange={(e) => set("q", e.target.value)}
            />
          </div>
          <label className="field">
            <span>Status</span>
            <select value={statusFilter} onChange={(e) => set("status", e.target.value)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="defunct">Defunct</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <div className="grow" />
          <div className="row small muted" style={{ gap: 6 }}>
            <span>Sort:</span>
            <select value={sort} onChange={(e) => toggleSort(e.target.value as SortKey)}>
              <option value="overall">Overall</option>
              <option value="reliability">Reliability</option>
              <option value="shipping">Shipping</option>
              <option value="fees">Fees</option>
              <option value="trustpilot">Trustpilot</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>
      </div>

      <div className="table-wrap sticky-1">
        <table className="data">
          <thead>
            <tr>
              <th onClick={() => toggleSort("name")}>Agent {arrow("name")}</th>
              <th onClick={() => toggleSort("overall")}>Overall {arrow("overall")}</th>
              {CATEGORY_COLS.map((c) => (
                <th key={c} onClick={() => toggleSort(c)} style={{ textTransform: "capitalize" }}>
                  {c} {arrow(c)}
                </th>
              ))}
              <th onClick={() => toggleSort("trustpilot")}>Trustpilot {arrow("trustpilot")}</th>
              <th className="no-sort">Coverage</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.agent.id}>
                <td>
                  <span className="row" style={{ gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={pins.has(r.agent.id)}
                      onChange={() => pins.toggle(r.agent.id)}
                      aria-label={`Pin ${r.agent.name}`}
                      style={{ flex: "none" }}
                    />
                    <Link to={`/agent/${r.agent.id}`} className="row" style={{ gap: 10 }}>
                      <Avatar text={r.agent.logoText ?? r.agent.name.slice(0, 2).toUpperCase()} />
                      <span>
                        <span className="row" style={{ gap: 6 }}>
                          <StatusDot status={r.agent.status.v} />
                          <strong style={{ color: "var(--text)" }}>{r.agent.name}</strong>
                        </span>
                        <span className="tiny faint">
                          {r.agent.status.v === "defunct" ? "defunct · " : ""}
                          {r.agent.foundingYear.v ? `est. ${r.agent.foundingYear.v}` : "est. unknown"}
                        </span>
                      </span>
                    </Link>
                  </span>
                </td>
                <td><ScoreBar score={r.overall} /></td>
                {CATEGORY_COLS.map((c) => (
                  <td key={c} className="num">
                    {r.cats[c] === null ? <span className="faint">—</span> : r.cats[c]!.toFixed(0)}
                  </td>
                ))}
                <td className="num">
                  {r.tp === null ? (
                    <span className="faint">—</span>
                  ) : (
                    <span title={`${r.agent.trustpilot.v?.reviews ?? 0} reviews (snapshot)`}>
                      {r.tp.toFixed(1)}<span className="tiny faint">/5</span>
                    </span>
                  )}
                </td>
                <td><Coverage value={r.coverage} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <div className="empty">No agents match your filters.</div>}

      <p className="tiny faint mt">
        Category cells show 0–100 sub-scores. “—” means insufficient known data to score that
        dimension (see Coverage). Trustpilot values are point-in-time snapshots.
      </p>
    </div>
  );
}
